/**
 * Home dashboard — a degraded-state overview. Phase 0 stub (cards),
 * Phase 6 fills in real degraded-state callouts.
 */
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { Link as RouterLink, useLoaderData } from 'react-router-dom';
import type { TenantDto } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';
import { useCopy } from '@/hooks/useCopy';
import { apiAdmin } from '@/api/admin';

export interface HomeLoaderData {
  tenants: TenantDto[];
}

export async function homeLoader(): Promise<HomeLoaderData> {
  const tenants = await apiAdmin.tenants.list().catch(() => []);
  return { tenants };
}

export function Home() {
  const { tenants } = useLoaderData() as HomeLoaderData;
  const { t } = useCopy();
  const active = tenants.filter((t0) => t0.status === 'ACTIVE');
  const suspended = tenants.filter((t0) => t0.status === 'SUSPENDED');
  const noProduct = tenants.filter((t0) => !t0.productId);
  const noBrand = tenants.filter((t0) => t0.productId && !t0.brandId);

  return (
    <Box>
      <PageHeader
        title={t('app.title', 'Journix SaaS Platform Admin')}
        subtitle={t('nav.home', 'Home')}
      />
      <Grid container spacing={2}>
        <DashboardCard title="Tenants" value={tenants.length} to="/tenants" />
        <DashboardCard title="Active" value={active.length} to="/tenants" />
        <DashboardCard title="Suspended" value={suspended.length} to="/tenants" />
        <DashboardCard title="Need product" value={noProduct.length} to="/tenants" />
        <DashboardCard title="Inheriting brand default" value={noBrand.length} to="/tenants" />
      </Grid>
      {(noProduct.length > 0 || suspended.length > 0) && (
        <Box sx={{ mt: 3 }}>
          {noProduct.length > 0 ? (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h3" gutterBottom>
                  Tenants missing a product
                </Typography>
                {noProduct.slice(0, 5).map((tn) => (
                  <Typography
                    key={tn.id}
                    component={RouterLink}
                    to={`/tenants/${tn.id}`}
                    sx={{ display: 'block' }}
                  >
                    {tn.name} ({tn.slug})
                  </Typography>
                ))}
              </CardContent>
            </Card>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

interface DashboardCardProps {
  title: string;
  value: number;
  to?: string;
}

function DashboardCard({ title, value, to }: DashboardCardProps) {
  const card = (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography
          variant="body2"
          gutterBottom
          sx={{
            color: 'text.secondary',
          }}
        >
          {title}
        </Typography>
        <Typography variant="h2">{value}</Typography>
      </CardContent>
    </Card>
  );
  if (to) {
    return (
      <Grid size={{ xs: 6, md: 3 }}>
        <Box component={RouterLink} to={to} sx={{ textDecoration: 'none', color: 'inherit' }}>
          {card}
        </Box>
      </Grid>
    );
  }
  return (
    <Grid size={{ xs: 6, md: 3 }}>
      {card}
    </Grid>
  );
}
